import React from 'react';
import { Datatable } from '../Datatable/Datatable';
import { Loading } from '../Loading/Loading';
import { Button } from '../Button/Button';

// Interfaces para as props
interface ActiveFilters {
    categories: any[];
    groups: any[];
    status: string | null;
}

interface TableHeader {
    key: string;
    value: string;
    type: string;
    noOrder?: boolean;
}

interface TableOption {
    icon: string;
    title: string;
    color: string;
    action?: (item: any) => void;
}

interface TableMeta {
    hasNextPage: boolean
    hasPreviousPage: boolean
    itemCount: number
    page: number
    pageCount: number
    take: number
}

interface ProductSearchCardProps {
    // Props principais
    searchValue?: string;
    onSearchChange: (value: string) => void;
    onSearch: () => void;
    searchResults?: any[];
    loading?: boolean;
    selectedCount?: number;

    // Props de filtros
    hasActiveFilters: boolean;
    activeFilters: ActiveFilters;
    onShowFilters: () => void;
    onClearFilters: () => void;

    // Props da tabela
    onSelectAll: (all: boolean, id: number, value: boolean) => void;
    onAddItem?: (itemId: string | number) => void;
    meta?: TableMeta;
    onTableDataChange: (take?: number, page?: number, search?: string, order?: any) => Promise<void>;

    // Props de customização
    title?: string;
    titleIcon?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    noResultsMessage?: string;

    // Props opcionais
    showSelectedButton?: boolean;
    onSelectedAction?: () => void;
    tableHeaders?: TableHeader[];
    tableOptions?: TableOption[];

    // Props de layout
    cardClassName?: string;
    maxTableHeight?: string;
}

const ProductSearchCard: React.FC<ProductSearchCardProps> = ({
    // Props principais
    searchValue = '',
    onSearchChange,
    onSearch,
    searchResults = [],
    loading = false,
    selectedCount = 0,

    // Props de filtros
    hasActiveFilters = false,
    activeFilters = { categories: [], groups: [], status: null },
    onShowFilters,
    onClearFilters,

    // Props da tabela
    onSelectAll,
    onAddItem,
    meta = null,
    onTableDataChange,

    // Props de customização
    title = 'Seus Produtos',
    titleIcon = 'fas fa-search',
    searchPlaceholder = 'Pesquisar produtos',
    emptyMessage = 'Pesquise por produtos para adicionar ao catálogo',
    noResultsMessage = 'Nenhum produto encontrado',

    // Props opcionais
    showSelectedButton = true,
    onSelectedAction,
    tableHeaders = [
        { key: 'Código', value: 'code', type: 'default' },
        { key: 'Produto', value: 'name', type: 'default' },
        { key: 'Ações', value: 'acoes', type: '', noOrder: true },
    ],
    tableOptions = [
        {
            icon: 'fas fa-plus',
            title: 'adicionar',
            color: 'success',
        },
    ],

    // Props de layout
    cardClassName = 'card col-6 mb-5',
    maxTableHeight = '400px',
}) => {

    const totalActiveFilters: number = activeFilters.categories.length +
        activeFilters.groups.length +
        (activeFilters.status ? 1 : 0);

    const handleTableOptions: TableOption[] = tableOptions.map((option: TableOption) => ({
        ...option,
        action: (item: any) => {
            if (option.action) {
                option.action(item);
            } else if (onAddItem) {
                onAddItem(item.id);
            }
        },
    }));

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        onSearchChange(event.target.value);
    };

    const handleClearSearch = (): void => {
        onSearchChange('');
        onSearch();
    };

    return (
        <div className={cardClassName}>
            <div className='card-header py-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>
                        <i className={`${titleIcon} me-2 text-info`}></i>
                        {title}
                    </span>
                </h3>

                <div className='col-md-4'>
                    <div className='form-group'>
                        <div className='d-flex gap-2'>
                            <button
                                className={`btn ${hasActiveFilters ? 'btn-warning' : 'btn-secondary'} w-100`}
                                type='button'
                                onClick={onShowFilters}
                                 style={{
                                    color: hasActiveFilters ? '#fff' : 'rgb(63, 66, 84)'
                                }}
                            >
                                <i className={`fas fa-filter me-2 ${hasActiveFilters ? 'btn-warning' : 'btn-secondary' }`}
                                style={{
                                    color: hasActiveFilters ? '#fff' : 'rgb(63, 66, 84)'
                                }}></i>
                                {hasActiveFilters ? 'Filtros Ativos' : 'Filtros'}
                                {hasActiveFilters && (
                                    <span className='badge badge-light-warning ms-2'>
                                        {totalActiveFilters}
                                    </span>
                                )}
                            </button>
                            {hasActiveFilters && (
                                <button
                                    className='btn btn-light-danger'
                                    type='button'
                                    onClick={onClearFilters}
                                    title='Limpar filtros'
                                >
                                    <i className='fas fa-times'></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className='card-body'>
                <div className='row'>
                    <div className='col-12'>
                        <div className='card'>
                            <div className='card-header px-0 pb-8'>
                                <h5 className='card-title mb-0 w-100'>
                                    <div className='d-flex align-items-center flex-col w-100 flex-wrap gap-4'>
                                        <div className='col-12 d-flex justify-content-between align-items-center'>
                                            <div className='form-group col-7'>
                                                <div className='input-group'>
                                                    <input
                                                        type='text'
                                                        className='form-control fw-normal'
                                                        style={{ backgroundColor: '#f3f6f9' }}
                                                        placeholder={searchPlaceholder}
                                                        value={searchValue}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <button
                                                        className='btn btn-primary'
                                                        type='button'
                                                        onClick={onSearch}
                                                        disabled={!searchValue.trim()}
                                                    >
                                                        <i className='fas fa-search'></i>
                                                    </button>
                                                    {searchValue && (
                                                        <button
                                                            className='btn btn-outline-danger'
                                                            type='button'
                                                            onClick={handleClearSearch}
                                                        >
                                                            <i className='fas fa-times'></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {showSelectedButton && selectedCount > 0 && onSelectedAction && (
                                                <Button
                                                    text={''}
                                                    color='primary'
                                                    horizontalPadding={4}
                                                    btnAttr={{
                                                        type: 'button',
                                                    }}
                                                    click={onSelectedAction}
                                                    btnClass='fw-bolder btn btn-primary'
                                                    icon='bi bi-box-arrow-right fs-2 d-flex align-items-center justify-content-center px-0'
                                                />
                                            )}
                                        </div>

                                        {hasActiveFilters && (
                                            <div className='col-7 d-flex align-items-center'>
                                                <div className='d-flex align-items-center'>
                                                    <div className='d-flex flex-wrap gap-2 align-items-center'>
                                                        <span className='fw-bold text-info' style={{ fontSize: '14px' }}>
                                                            Filtros ativos:
                                                        </span>
                                                        {activeFilters.categories.length > 0 && (
                                                            <span className='badge badge-info'>
                                                                {activeFilters.categories.length} Categoria(s)
                                                            </span>
                                                        )}
                                                        {activeFilters.groups.length > 0 && (
                                                            <span className='badge badge-warning'>
                                                                {activeFilters.groups.length} Grupo(s)
                                                            </span>
                                                        )}
                                                        {activeFilters.status && (
                                                            <span className='badge badge-success'>
                                                                Status: {activeFilters.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </h5>
                            </div>

                            <div className='card-body p-3'>
                                {loading ? (
                                    <div className='d-flex justify-content-center p-5'>
                                        <Loading />
                                    </div>
                                ) : searchResults.length > 0 ? (
                                    <Datatable
                                        selectable={true}
                                        onSelectable={onSelectAll}
                                        data={searchResults}
                                        headers={tableHeaders}
                                        options={handleTableOptions}
                                        color='primary'
                                        showControls={false}
                                        //onChangeData={onTableDataChange}
                                        headerFixed
                                        maxHeight={maxTableHeight}
                                    />
                                ) : (
                                    <div className='text-center p-5'>
                                        <i className='fas fa-search fa-3x text-muted mb-3'></i>
                                        <p className='text-muted'>
                                            {searchValue ? noResultsMessage : emptyMessage}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductSearchCard;